<template>
    <div>
        <!-- Toast Flash -->
        <div
            v-if="shouldShowFlash"
            ref="flash"
            id="flash"
            class="toast-modern"
            :class="flashClass"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
        >
            <div class="toast-content">
                <div class="toast-icon">
                    <i :class="flashIcon"></i>
                </div>
                
                <div class="toast-body-modern">
                    <div class="toast-time">{{ flash.time }}</div>
                    
                    <template v-if="flash.content">
                        <div v-if="flash.content.message" class="toast-message">
                            {{ flash.content.message }}
                        </div>

                        <ul v-if="flash.content.errors" class="toast-errors">
                            <template v-for="(errors, field) in flash.content.errors">
                                <li v-for="error in errors" :key="error">
                                    <strong>{{ field }}</strong>: {{ error }}
                                </li>
                            </template>
                        </ul>
                    </template>
                </div>
                
                <button type="button" class="btn-close-modern" @click="close" aria-label="Close">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="toast-progress">
                <div class="toast-progress-bar" :style="{ width: progressWidth + '%' }"></div>
            </div>
            
            <!-- Report Problem Button (solo se loggato E errore) -->
            <button 
                v-if="isLoggedIn && flash.type === 'danger'"
                @click.stop="showReportForm = true"
                class="btn-report-problem"
                type="button"
            >
                <i class="fas fa-bug me-2"></i>
                Segnala un problema
            </button>
        </div>

        <!-- Report Form Modal -->
        <div v-if="showReportForm" class="modal-overlay" @click="closeReportForm">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>Segnala un problema</h3>
                    <button type="button" class="btn-close-modal" @click="closeReportForm">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                
                <form @submit.prevent="submitReport" class="report-form">
                    <div class="form-group">
                        <label for="reportTitle">Titolo</label>
                        <input
                            type="text"
                            id="reportTitle"
                            v-model="reportForm.title"
                            class="form-control"
                            placeholder="Breve descrizione del problema"
                            required
                        >
                    </div>

                    <div class="form-group">
                        <label for="reportDescription">Descrizione</label>
                        <textarea
                            id="reportDescription"
                            v-model="reportForm.description"
                            class="form-control"
                            rows="6"
                            placeholder="Descrivi il problema in dettaglio..."
                            required
                        ></textarea>
                    </div>

                    <div class="form-group">
                        <label for="reportFiles">Allega file (opzionale)</label>
                        <div class="file-upload-info">
                            <i class="fas fa-shield-alt text-warning me-2"></i>
                            <small>
                                <strong>File protetti:</strong> I file caricati saranno visibili solo allo staff. 
                                Massimo 5 foto (PNG/JPG/JPEG), 20MB per file.
                                <br>
                                <strong>Attenzione:</strong> Evita di mostrare informazioni sensibili negli screenshot.
                            </small>
                        </div>
                        
                        <input
                            type="file"
                            id="reportFiles"
                            ref="fileInput"
                            @change="handleFileSelection"
                            class="file-input-hidden"
                            accept=".png,.jpg,.jpeg"
                            multiple
                        >
                        
                        <button 
                            type="button" 
                            class="btn-file-upload"
                            @click="$refs.fileInput.click()"
                        >
                            <i class="fas fa-cloud-upload-alt me-2"></i>
                            Scegli immagini
                        </button>
                        
                        <!-- File List with Progress -->
                        <div v-if="selectedFiles.length > 0" class="selected-files-list">
                            <div 
                                v-for="(file, index) in selectedFiles" 
                                :key="index"
                                class="file-item"
                                :class="{ 'file-error': file.error, 'file-uploading': file.uploading, 'file-success': file.uploaded }"
                            >
                                <div class="file-info">
                                    <i :class="getFileIcon(file.file)"></i>
                                    <span class="file-name">{{ file.file.name }}</span>
                                    <span class="file-size">{{ formatFileSize(file.file.size) }}</span>
                                </div>
                                
                                <div v-if="file.error" class="file-status">
                                    <i class="fas fa-exclamation-circle text-danger"></i>
                                    <small class="text-danger">{{ file.error }}</small>
                                </div>
                                
                                <div v-else-if="file.uploading" class="file-progress">
                                    <div class="progress">
                                        <div 
                                            class="progress-bar progress-bar-striped progress-bar-animated" 
                                            :style="{ width: file.progress + '%' }"
                                        ></div>
                                    </div>
                                    <small>{{ file.progress }}%</small>
                                </div>
                                
                                <div v-else-if="file.uploaded" class="file-status">
                                    <i class="fas fa-check-circle text-success"></i>
                                    <small class="text-success">Caricato</small>
                                </div>
                                
                                <button 
                                    v-if="!file.uploading"
                                    type="button" 
                                    class="btn-remove-file"
                                    @click="removeFile(index)"
                                >
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn-cancel" @click="closeReportForm" :disabled="uploadingReport">
                            Annulla
                        </button>
                        <button type="submit" class="btn-submit" :disabled="uploadingReport">
                            <span v-if="uploadingReport">
                                <i class="fas fa-spinner fa-spin me-2"></i>
                                Invio in corso...
                            </span>
                            <span v-else>
                                <i class="fas fa-paper-plane me-2"></i>
                                Invia segnalazione
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import {Toast} from 'bootstrap'

export default {
    name: 'ComponentFlash',

    data() {
        return {
            toast: null,
            progressWidth: 100,
            progressInterval: null,
            showReportForm: false,
            reportForm: {
                title: '',
                description: ''
            },
            selectedFiles: [],
            uploadingReport: false,
            uploadedFileIds: []
        }
    },
    
    computed: {
        flash() {
            return this.$store.state.flash
        },

        isLoggedIn() {
            return this.$store.getters.isLoggedIn
        },

        shouldShowFlash() {
            return this.flash !== false && 
                   this.flash.content && 
                   (this.flash.content.message || this.flash.content.errors)
        },
    
        flashClass() {
            if (!this.flash) return 'toast-info'
            
            if (this.flash.type === 'danger') {
                return 'toast-danger'
            } else if (this.flash.type === 'success') {
                return 'toast-success'
            } else if (this.flash.type === 'warning') {
                return 'toast-warning'
            } else {
                return 'toast-info'
            }
        },

        flashIcon() {
            if (!this.flash) return 'fas fa-info-circle'
            
            if (this.flash.type === 'danger') {
                return 'fas fa-exclamation-circle'
            } else if (this.flash.type === 'success') {
                return 'fas fa-check-circle'
            } else if (this.flash.type === 'warning') {
                return 'fas fa-exclamation-triangle'
            } else {
                return 'fas fa-info-circle'
            }
        }
    },
    
    watch: {
        flash: {
            handler() {
                if (this.shouldShowFlash) {
                    this.$nextTick(() => {
                        this.show()
                    })
                } else if (this.toast) {
                    this.toast.hide()
                }
            },
            deep: true
        }
    },

    methods: {
        show() {
            if (!this.$refs.flash) return
            
            if (!this.toast) {
                this.toast = new Toast(this.$refs.flash, {
                    animation: true,
                    autohide: false, // Gestiamo manualmente la chiusura
                    delay: 8000
                })
            }
            
            this.toast.show()
            this.startProgress()
        },

        close() {
            this.stopProgress()
            if (this.toast) {
                this.toast.hide()
            }
            this.$store.commit('setFlash', false)
        },

        startProgress() {
            this.stopProgress()
            this.progressWidth = 100
            
            const duration = 8000 // 8 secondi
            const intervalTime = 50 // Aggiorna ogni 50ms
            const decrement = (100 / duration) * intervalTime
            
            this.progressInterval = setInterval(() => {
                this.progressWidth -= decrement
                if (this.progressWidth <= 0) {
                    this.progressWidth = 0
                    this.stopProgress()
                    // Chiudi il toast quando la barra finisce
                    this.close()
                }
            }, intervalTime)
        },

        stopProgress() {
            if (this.progressInterval) {
                clearInterval(this.progressInterval)
                this.progressInterval = null
            }
        },

        closeReportForm() {
            this.showReportForm = false
            this.reportForm.title = ''
            this.reportForm.description = ''
            this.selectedFiles = []
            this.uploadedFileIds = []
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },

        handleFileSelection(event) {
            const files = Array.from(event.target.files)
            
            // Verifica limite totale (esistenti + nuovi)
            if (this.selectedFiles.length + files.length > 5) {
                this.$store.dispatch('flashWarning', {
                    message: 'Puoi caricare massimo 5 file in totale'
                })
                event.target.value = ''
                return
            }
            
            // Valida ogni file e aggiungi alla lista esistente
            const newFiles = files.map(file => {
                const validTypes = ['image/png', 'image/jpeg', 'image/jpg']
                const maxSize = 20 * 1024 * 1024 // 20MB
                
                let error = null
                if (!validTypes.includes(file.type)) {
                    error = 'Solo immagini PNG/JPG/JPEG'
                } else if (file.size > maxSize) {
                    error = 'File troppo grande (max 20MB)'
                }
                
                return {
                    file: file,
                    error: error,
                    uploading: false,
                    uploaded: false,
                    progress: 0
                }
            })
            
            // Aggiungi ai file esistenti invece di sostituirli
            this.selectedFiles.push(...newFiles)
            
            // Resetta input per permettere di selezionare gli stessi file
            event.target.value = ''
        },

        removeFile(index) {
            this.selectedFiles.splice(index, 1)
            // Resetta l'input se non ci sono più file
            if (this.selectedFiles.length === 0 && this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },

        async uploadFiles() {
            const validFiles = this.selectedFiles.filter(f => !f.error)
            if (validFiles.length === 0) return
            
            const formData = new FormData()
            validFiles.forEach((fileObj) => {
                formData.append('files[]', fileObj.file)
            })
            formData.append('protected', true)
            
            try {
                // Imposta tutti i file in uploading
                validFiles.forEach(f => f.uploading = true)
                
                const response = await axios.post('/files/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        // Aggiorna il progresso per tutti i file
                        validFiles.forEach(f => f.progress = progress)
                    }
                })
                
                if (response.data.files && response.data.files.length > 0) {
                    response.data.files.forEach((file, index) => {
                        this.uploadedFileIds.push(file.id)
                        if (validFiles[index]) {
                            validFiles[index].uploaded = true
                            validFiles[index].uploading = false
                            validFiles[index].progress = 100
                        }
                    })
                }
            } catch (error) {
                validFiles.forEach(f => {
                    f.error = 'Errore durante il caricamento'
                    f.uploading = false
                })
            }
        },

        async submitReport() {
            this.uploadingReport = true;
            // Upload files prima di inviare il report
            if (this.selectedFiles.length > 0) {
                await this.uploadFiles()
            }
            
            try {
                // Invia il report con title, description e uploadedFileIds
                const response = await axios.post('/reports', {
                    title: this.reportForm.title,
                    description: this.reportForm.description,
                    file_ids: this.uploadedFileIds
                })
                
                this.closeReportForm()
                this.$store.dispatch('flashSuccess', {
                    message: response.data.message || 'Segnalazione inviata con successo. Grazie per il tuo feedback!'
                })
            } catch (error) {
                if (error.response && error.response.status === 429) {
                    this.$store.dispatch('flashDanger', {
                        message: error.response.data.error || 'Troppi tentativi. Riprova più tardi.'
                    })
                } else {
                    this.$store.dispatch('flashDanger', {
                        message: 'Errore durante l\'invio della segnalazione. Riprova.'
                    })
                }
            } finally {
                this.uploadingReport = false
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
        },

        getFileIcon(file) {
            if (file.type.startsWith('image/')) {
                return 'fas fa-image text-primary'
            } else if (file.type.startsWith('video/')) {
                return 'fas fa-video text-danger'
            }
            return 'fas fa-file'
        }
    },

    beforeDestroy() {
        this.stopProgress()
        this.$store.commit('setFlash', false)
    }
}
</script>

<style scoped>
.toast-modern {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    min-width: 320px;
    max-width: 400px;
    z-index: 9999;
    border: none;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    animation: slideInRight 0.4s ease-out;
    overflow: hidden;
}

.toast-content {
    display: flex;
    align-items: flex-start;
    padding: 1.25rem;
    gap: 1rem;
}

.toast-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    font-size: 1.25rem;
}

.toast-body-modern {
    flex: 1;
    min-width: 0;
}

.toast-time {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-bottom: 0.25rem;
    font-weight: 500;
}

.toast-message {
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.4;
    margin: 0;
}

.toast-errors {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0 0 0;
    font-size: 0.875rem;
}

.toast-errors li {
    margin-bottom: 0.25rem;
    opacity: 0.9;
}

.toast-errors strong {
    font-weight: 600;
}

.btn-close-modern {
    flex-shrink: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s ease;
    padding: 0.25rem;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-close-modern:hover {
    opacity: 1;
}

.btn-close-modern i {
    font-size: 0.875rem;
}

/* Success variant */
.toast-success {
    background: linear-gradient(135deg, #1a4d1a 0%, #2d5f2d 100%);
    color: white;
}

.toast-success .toast-icon {
    background: rgba(72, 187, 120, 0.2);
    color: #48bb78;
}

.toast-success .btn-close-modern {
    color: white;
}

.toast-success .btn-close-modern:hover {
    background: rgba(255, 255, 255, 0.1);
}

/* Danger variant */
.toast-danger {
    background: linear-gradient(135deg, #4d1a1a 0%, #5f2d2d 100%);
    color: white;
}

.toast-danger .toast-icon {
    background: rgba(245, 101, 101, 0.2);
    color: #f56565;
}

.toast-danger .btn-close-modern {
    color: white;
}

.toast-danger .btn-close-modern:hover {
    background: rgba(255, 255, 255, 0.1);
}

/* Warning variant */
.toast-warning {
    background: linear-gradient(135deg, #4d3d1a 0%, #5f4f2d 100%);
    color: white;
}

.toast-warning .toast-icon {
    background: rgba(237, 137, 54, 0.2);
    color: #ed8936;
}

.toast-warning .btn-close-modern {
    color: white;
}

.toast-warning .btn-close-modern:hover {
    background: rgba(255, 255, 255, 0.1);
}

/* Info variant */
.toast-info {
    background: linear-gradient(135deg, #1a3d4d 0%, #2d4f5f 100%);
    color: white;
}

.toast-info .toast-icon {
    background: rgba(66, 153, 225, 0.2);
    color: #4299e1;
}

.toast-info .btn-close-modern {
    color: white;
}

.toast-info .btn-close-modern:hover {
    background: rgba(255, 255, 255, 0.1);
}

/* Progress Bar */
.toast-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    overflow: hidden;
}

.toast-progress-bar {
    height: 100%;
    background: rgba(255, 255, 255, 0.5);
    transition: width 50ms linear;
    border-radius: 0 0 16px 16px;
}

.toast-success .toast-progress-bar {
    background: #48bb78;
}

.toast-danger .toast-progress-bar {
    background: #f56565;
}

.toast-warning .toast-progress-bar {
    background: #ed8936;
}

.toast-info .toast-progress-bar {
    background: #4299e1;
}

/* Animations */
@keyframes slideInRight {
    from {
        opacity: 0;
        transform: translateX(100%);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* Responsive */
@media (max-width: 576px) {
    .toast-modern {
        bottom: 1rem;
        right: 1rem;
        left: 1rem;
        min-width: auto;
        max-width: none;
    }
}

/* Hide animation when dismissed */
.toast-modern.hiding {
    animation: slideOutRight 0.3s ease-out forwards;
}

/* Report Problem Button */
.btn-report-problem {
    width: 100%;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 16px 16px;
}

.btn-report-problem:hover {
    background: rgba(255, 255, 255, 0.15);
}

.btn-report-problem i {
    font-size: 0.875rem;
}

/* Modal Overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(5px);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    animation: fadeIn 0.3s ease-out;
}

.modal-content {
    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
    border: 2px solid #D3B100;
    box-shadow: 0 4px 20px rgba(211, 177, 0, 0.4);
    border-radius: 20px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease-out;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 2rem;
    border-bottom: 2px solid #D3B100;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #FFD700;
}

.btn-close-modal {
    background: transparent;
    border: none;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: #6c757d;
    transition: all 0.2s ease;
}

.btn-close-modal:hover {
    background: #f0f0f0;
    color: #1a1a1a;
}

.btn-close-modal i {
    font-size: 1.25rem;
}

.report-form {
    overflow-y: scroll;
    padding: 2rem;
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;
}

.report-form::-webkit-scrollbar { 
    display: none;  /* Safari and Chrome */
}

.report-form .form-group {
    margin-bottom: 1.5rem;
}

.report-form label {
    display: block;
    margin-bottom: 0.5rem;
    color: #D3B100;
    font-weight: 600;
    font-size: 0.95rem;
}

.report-form .form-control {
    width: 100%;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    border: 1px solid #D3B100 ;
    border-radius: 12px;
    transition: all 0.3s ease;
    background: rgba(211, 177, 0, 0.1);
    font-family: inherit;
}

.report-form .form-control:focus {
    outline: none;
    border-color: #D3B100;
    box-shadow: 0 0 0 4px rgba(211, 177, 0, 0.1);
}

.report-form textarea.form-control {
    resize: vertical;
    min-height: 120px;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
}

.btn-cancel {
    padding: 0.875rem 1.5rem;
    background: white;
    color: #495057;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-cancel:hover {
    border-color: #D3B100;
    color: #D3B100;
}

.btn-submit {
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #D3B100 0%, #FFD700 100%);
    color: #1a1a1a;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(211, 177, 0, 0.3);
    display: flex;
    align-items: center;
}

.btn-submit:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(211, 177, 0, 0.4);
}

.btn-submit:disabled,
.btn-cancel:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive */
@media (max-width: 576px) {
    .btn-report-problem span {
        display: none;
    }
    
    .modal-header,
    .report-form {
        padding: 1.5rem;
    }
    
    .modal-actions {
        flex-direction: column;
    }
    
    .btn-cancel,
    .btn-submit {
        width: 100%;
        justify-content: center;
    }
}

@keyframes slideOutRight {
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(100%);
    }
}

/* File Upload Section */
.file-input-hidden {
    display: none;
}

.btn-file-upload {
    width: 100%;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, rgba(211, 177, 0, 0.15) 0%, rgba(255, 215, 0, 0.15) 100%);
    border: 2px dashed rgba(211, 177, 0, 0.5);
    color: #FFD700;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
}

.btn-file-upload:hover {
    background: linear-gradient(135deg, rgba(211, 177, 0, 0.25) 0%, rgba(255, 215, 0, 0.25) 100%);
    border-color: #FFD700;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(211, 177, 0, 0.2);
}

.btn-file-upload i {
    font-size: 1.25rem;
}

.file-upload-info {
    display: flex;
    align-items: start;
    padding: 0.875rem;
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.3);
    border-radius: 8px;
    margin-bottom: 1rem;
}

.file-upload-info small {
    color: #e0e0e0;
    line-height: 1.5;
}

.file-upload-info strong {
    color: #FFD700;
}

.selected-files-list {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.file-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(211, 177, 0, 0.3);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.file-item:hover {
    background: rgba(255, 255, 255, 0.08);
}

.file-item.file-error {
    border-color: #f56565;
    background: rgba(245, 101, 101, 0.1);
}

.file-item.file-success {
    border-color: #48bb78;
    background: rgba(72, 187, 120, 0.1);
}

.file-item.file-uploading {
    border-color: #4299e1;
}

.file-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;
}

.file-info i {
    font-size: 1.25rem;
    flex-shrink: 0;
}

.file-name {
    color: #e0e0e0;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
}

.file-size {
    color: #9ca3af;
    font-size: 0.875rem;
    flex-shrink: 0;
}

.file-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.file-progress {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    min-width: 150px;
}

.file-progress .progress {
    flex: 1;
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.file-progress .progress-bar {
    background: linear-gradient(90deg, #D3B100 0%, #FFD700 100%);
}

.file-progress small {
    color: #9ca3af;
    font-size: 0.875rem;
    min-width: 40px;
    text-align: right;
}

.btn-remove-file {
    background: transparent;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.btn-remove-file:hover {
    background: rgba(245, 101, 101, 0.2);
    color: #f56565;
}
</style>

